import {
  NILoyaltyLoginIcon,
  NILoyaltyOfferIcon,
} from '@native-instruments/webex-ni';
import clsx from 'clsx';
import { Link, useLoaderData } from 'react-router';
import { getLoginUrl } from '~/helpers/auth';
import { useIntl } from '~/helpers/intl';
import { isTruthy } from '~/helpers/predicate';
import { getLoyaltyVariant } from '~/helpers/product';
import { useCurrentUrl } from '~/hooks/useCurrentUrl';
import { OWN_BRANDS, type loader } from '../../route';
import { AddToCartButton } from '../AddToCartButton';
import { FindDealer } from '../FindDealer';
import { PriceListing } from '../PriceListing';
import { ProductAvailability } from '../ProductAvailability';

export function ProductSummary() {
  const { t, tPrice, tReact } = useIntl();
  const { currentUrl } = useCurrentUrl();

  const {
    linkedProducts,
    product,
    variant,
    countryRelatedInfo,
    addToCartSku,
    page,
    showRetailerLink,
  } = useLoaderData<typeof loader>();
  const loginUrl = getLoginUrl({ returnTo: currentUrl });
  const loyaltyVariant = getLoyaltyVariant(product);
  const loyaltyPrice = product.price?.loyalty;
  const isOnSale = variant?.onSale ?? product.onSale;
  const shouldShowLabel = isTruthy(product.label) || isOnSale;

  // Determine which loyalty badge to show - personalized takes precedence over general
  const showPersonalizedLoyaltyBadge = isTruthy(
    loyaltyVariant?.relatedOwnedProductName,
  );
  const showGeneralLoyaltyBadge =
    isTruthy(loyaltyPrice) && !showPersonalizedLoyaltyBadge;

  const partnerBrand = OWN_BRANDS.includes(page.brand?.name ?? '')
    ? null
    : page.brand?.name;

  return (
    <>
      {shouldShowLabel && (
        <div
          className="rounded bg-sale py-0.5 px-1 text-xs font-medium text-grey-10 w-fit mb-1"
          data-testid="product-label"
        >
          {isTruthy(product.label)
            ? product.label
            : t('catalog.filterOnSale', {
                fallback: 'On sale',
              })}
        </div>
      )}

      <h1 className="webex-h2" data-testid="product-title">
        {product.name}
      </h1>

      {partnerBrand && (
        <p className="font-medium text-grey-60">
          {t('pricing.byPartnerBrand', {
            fallback: 'by {partnerBrand}',
            values: {
              partnerBrand: partnerBrand,
            },
          })}
        </p>
      )}

      <p
        className="text-grey-70 text-ellipsis mb-3 mt-2 text-xs"
        data-testid="product-desc"
      >
        {product.description}
      </p>

      {linkedProducts?.length > 0 && (
        <div
          data-testid="linked-products"
          className="flex mb-3 flex-col gap-1.5"
        >
          {linkedProducts.map(
            ({ slug, name, shortDescription, currency, value }) => {
              const isCurrentProduct = slug === page.slug;

              return (
                <Link
                  data-testid={`linked-product-${slug}`}
                  key={slug}
                  to={`../${slug}/`}
                  relative="path"
                  className={clsx(
                    isCurrentProduct &&
                      'outline outline-1 outline-grey-90 border-grey-90',
                    'flex place-content-between rounded border border-solid bg-white p-3 border-grey-40 py-2 px-3 gap-3 hover:outline hover:outline-1 hover:outline-grey-90 hover:border-grey-90',
                  )}
                >
                  <div className="flex flex-col">
                    <span className="font-medium">{name}</span>
                    {shortDescription && (
                      <span className="text-2xs pt-1">{shortDescription}</span>
                    )}
                  </div>
                  <span className="self-center font-medium">
                    {currency &&
                      tPrice({
                        price: {
                          currency,
                          value,
                        },
                      })}
                  </span>
                </Link>
              );
            },
          )}
        </div>
      )}

      {showPersonalizedLoyaltyBadge && (
        <div
          data-testid="loyalty-offer-auth"
          className="my-3 p-2 rounded-sm bg-grey-20 w-full text-xs flex justify-content items-center"
        >
          <div className="mr-1">
            <NILoyaltyOfferIcon />
          </div>

          {tReact('pdp.loyaltyBannerSuccess', {
            fallback:
              'Because you own {productName}, you qualify for a special loyalty price.',
            values: {
              productName: (
                <span className="font-medium" data-testid="own-product">
                  {loyaltyVariant.relatedOwnedProductName}
                </span>
              ),
            },
          })}
        </div>
      )}

      {showGeneralLoyaltyBadge && loyaltyPrice && (
        <div
          data-testid="loyalty-offer-unauth"
          className="my-3 p-2 rounded bg-grey-20 w-full text-xs flex justify-content items-center"
        >
          <div className="mr-1">
            <NILoyaltyLoginIcon />
          </div>

          {tReact('pdp.loyaltybanner', {
            fallback:
              'Loyalty pricing from {value}. {loginLink} to see if you qualify.',
            values: {
              value: tPrice({ price: loyaltyPrice }),
              loginLink: (
                <a
                  href={loginUrl}
                  className="font-medium underline"
                  data-testid="loyalty-login-link"
                >
                  {t('common.link.login', {
                    fallback: 'Login',
                  })}
                </a>
              ),
            },
          })}
        </div>
      )}

      <div className="hidden lg:block">
        <div className="my-3 w-full flex items-center text-sm font-bold justify-center">
          <PriceListing product={product} variant={variant} />
        </div>

        <AddToCartButton
          product={product}
          variant={variant}
          data-testid={`add-to-cart-desktop-${addToCartSku}`}
        />
      </div>

      {showRetailerLink && (
        <div>
          <hr className="h-px my-3 border-grey-40" />

          <FindDealer productName={product.name} />
        </div>
      )}

      {variant?.ownedByUser === true ? (
        <p
          className="flex justify-center text-xs text-success font-medium py-3"
          data-testid="already-own-msg"
        >
          {t('pages.whatsnewforme.alreadyOwn', {
            fallback: 'You already own this product.',
          })}
        </p>
      ) : (
        <ProductAvailability
          variant={variant}
          product={product}
          countryRelatedInfo={countryRelatedInfo}
        />
      )}
    </>
  );
}
